<template>
    <Tooltip class="p-4 pointer-events-none" position="left">
        <template v-slot:trigger>
            <!-- DETAILS MODAL -->
            <Modal v-if="itemLevelIssues.length > 0"
                   :visible="detailsVisible"
                   @update:visible="v => detailsVisible = v"
            >
                <template v-slot:title>
                    {{ title }}
                </template>
                <table class="table table-hover table-vertical-center">
                    <thead>
                    <tr>
                        <th class="text-left">
                            {{ __('general.servability') }}
                        </th>
                        <th class="text-left">
                            {{ __('general.description') }}
                        </th>
                        <th class="text-right">
                            {{ __('general.affected') }}
                        </th>
                        <th>
                            {{ __('general.documentation') }}
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="issue in itemLevelIssues">
                        <td class="text-left">
                            <Badge v-if="issue.servability === 'disapproved'"
                                   type="bg-light-danger text-danger">
                                {{ issue.servability.toUpperCase() }}
                            </Badge>
                            <Badge v-else>
                                {{ issue.servability.toUpperCase() }}
                            </Badge>
                        </td>
                        <td class="text-left">{{ issue.description }}</td>
                        <td class="text-right text-monospace">
                            {{ issue.total.toLocaleString() }}
                        </td>
                        <td>
                            <a v-if="issue.documentation" :href="issue.documentation" target="_new">
                                <i class="fas fa-external-link-alt"/>
                            </a>
                            <span v-else class="text-muted">
                                &dash;
                            </span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </Modal>

            <!-- TRIGGER / PREVIEW -->
            <div @click="detailsVisible = true" class="cursor-pointer">
                <div class="text-center">
                    {{ totalProducts.toLocaleString() }}&nbsp;{{ __('general.product.multi') }}
                </div>
                <div class="d-flex flex-column gap-1 my-1 w-100">
                    <div v-for="(productCount, productStatus) in addonStatus?.statistics || {}">
                        <div class="bg-light-light rounded-sm w-100" style="height: .5em;">
                            <div class="bg-success rounded-sm h-100"
                                 :class="[colorClass(productStatus)]"
                                 :style="`width:${barWidth(productCount)};`"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- TOOLTIP CONTENTS -->
        <div v-if="addonStatus">
            <div class="font-size-lg text-center">
                {{ title }}
            </div>
            <div class="font-size-lg font-weight-bold mb-4 text-center">
                {{ totalProducts.toLocaleString() }}&nbsp;{{ __('general.product.multi') }}
            </div>
            <table class="table table-sm table-dark text-nowrap m-0">
                <tbody>
                <tr v-for="(productCount, productStatus) in addonStatus?.statistics || {}"
                    :class="{'d-none': productCount === 0}"
                >
                    <td class="px-2 font-weight-bold text-left">
                        <Badge :type="colorClass(productStatus)"/>
                        {{ __(`integrations.mc.product_statuses.${productStatus}`) }}
                    </td>
                    <td class="px-2 font-weight-bold text-right text-monospace">
                        {{ productCount.toLocaleString() }}
                    </td>
                    <td class="px-2 text-right text-monospace">
                        {{ percentage(productCount) }}%
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    </Tooltip>
</template>

<script>
import Tooltip from "@components/Tooltip.vue";
import Badge from "@components/Badge.vue";
import Modal from "@components/Modal.vue";

export default {
    components: {Modal, Badge, Tooltip},

    props: {
        title: String,
        addonStatus: {
            type: Object,
            default() {
                return {
                    "channel": "",
                    "destination": "",
                    "country": "",
                    "statistics": {
                        "active": 0,
                        "pending": 0,
                        "disapproved": 0,
                        "expiring": 0
                    },
                    "item_level_issues": []
                }
            }
        }
    },

    data() {
        return {
            detailsVisible: false
        }
    },

    methods: {
        percentage(productsCount) {
            if (this.totalProducts === 0 || productsCount === 0) {
                return 0.0;
            }

            return parseFloat((productsCount * 100 / this.totalProducts).toFixed(2));
        },

        barWidth(productsCount) {
            let percentage = this.percentage(productsCount);

            if (percentage === 0) {
                return 0;
            }

            if (percentage < 5) {
                //Force minimum bar size for better visibility.
                percentage = 5.0;
            }

            return `${percentage}%`;
        },

        colorClass(productStatus) {
            switch (productStatus) {
                case 'active':
                    return 'bg-success';
                case 'pending':
                    return 'bg-warning';
                case 'disapproved':
                    return 'bg-danger';
                default:
                    return 'bg-info';
            }
        }
    },

    computed: {
        totalProducts() {
            if (this.addonStatus === null) {
                return 0;
            }

            return Object.values(this.addonStatus.statistics).reduce((prev, current) => prev + current, 0)
        },

        itemLevelIssues() {
            return (this.addonStatus?.item_level_issues || [])
                .slice(0) //Creates copy of array.
                .sort((a, b) => {
                    //Sort by 'servability' then by 'total'.
                    let aOffset = a.servability === 'disapproved' ? 1_000_000 : 0;
                    let bOffset = b.servability === 'disapproved' ? 1_000_000 : 0;

                    return (b.total + bOffset) - (a.total + aOffset);
                });
        }
    }
}
</script>
